import { Currency, HorizontalAlignment, Model, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutManualOfficeSettlement } from "./autogen/AutogenLayoutManualOfficeSettlement";
import { getPayrollControlBoolean, getPayrollControlValue } from "@mcleod/general";
import {
    BlurEvent, ChangeEvent, ClickEvent, DataDisplayEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode,
    DataSourceValidationEvent, DropdownSelectionEvent, Layout, LookupModelSearchEvent, LookupModelSelectionEvent,
    Snackbar, TableRow, TableRowDisplayEvent, Textbox, ValidationResult
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutJobClassLookup } from "@mcleod/settlement/src/autogen/AutogenLayoutJobClassLookup";

const DEDUCT_TYPE_REIMBURSE = "R";
const DEDUCT_TYPE_EARNING = "E";
const CONTRIB_401K = "4";
const CONTRIB_ROTH = "R";
const HOURLY_PAY_METHOD = "H";
const SALARY_PAY_METHOD = "S";

// Payment Frequency Codes
const BI_WEEKLY = "B";
const SEMI_MONTHLY = "S";
const MONTHLY = "M";
const QUARTERLY = "Q";
const ANNUALLY = "A";
const WEEKLY = "W";

const PAY_FREQUENCY_CODES = [ BI_WEEKLY, SEMI_MONTHLY, MONTHLY, QUARTERLY, ANNUALLY, WEEKLY ];

export class ManualOfficeSettlement extends AutogenLayoutManualOfficeSettlement {

    private contribution401k = new Currency(0);
    private contribution401kCatchup = new Currency(0);
    private deductionTotal = new Currency(0);
    private earningsTotal = new Currency(0);
    private fedTaxTable: ModelRow;


    override async onLoad(): Promise<void> {

        this.fedTaxTable = await Model.searchSingleRecord("lme/settlement/fedtaxtable");

        this.textboxAdditionalTaxOverrideHours.visible = getPayrollControlBoolean("addl_tax_override_hours", false);

        this.mainDataSource.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            if (event.getAction() === DataSourceAction.SEARCH) {
                this.buttonClear.enabled =
                !this.activeRow.isNull("off_man_timecard.division_id") ||
                !this.activeRow.isNull("off_man_timecard.terminal_id") ||
                !this.activeRow.isNull("off_man_timecard.job_class_code_id");
            }
        });

        this.mainDataSource.doBeforeDataSourcePost = async () => {
            if (this.activeRow.getCurrency("net_pay", new Currency(0)).isNegative()) {
                if (this.activeRow.getBoolean("post_to_gl")) {
                    Snackbar.showSnackbar("Because you have selected to create GL entries, the 'Amount Owed' field "
                        + "on the Office Payee screen will be updated to reflect the negative Check Amount.", { targetPanel:this });
                } else {
                    Snackbar.showSnackbar("Because GL entries are not being created, 'Amount Owed' for the payee will NOT be updated. "
                        + "The 'Amount Owed' field can be manually updated from the Office Payee screen if an amount is due because of this transaction.", { targetPanel:this });
                }
            }
            return true;
        }

        this.setupFieldListeners()
        this.addValidations();

    }

    setupFieldListeners() {
        this.addLayoutDataLoadListener(() => {
            this.calcTotals();
        });

        this.tablePayAdjustments.addContentsChangedListener(() => {
            this.calcTotals();

        });
        this.table401kDetails.addContentsChangedListener(() => {
            this.calcTotals();
        });
        this.textboxOldBalApplied.addBlurListener(() => this.calcTotals());
        this.textboxStateTaxId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            event.filter.type_of = "S";
        });
        this.textboxCountyTaxId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            event.filter.type_of = "O";
        });
        this.textboxCityTaxId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            event.filter.type_of = "C";
        });
        this.textboxSuiTaxCode.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            event.filter.type_of = "S";
        });

        this.checkboxCreateGlEntries.addChangeListener((event: ChangeEvent) => {
            this.textboxDivision.required = this.checkboxCreateGlEntries.checked;
            this.textboxTerminal.required = this.checkboxCreateGlEntries.checked;
            this.textboxJobClass.required = this.checkboxCreateGlEntries.checked;
            this.textboxPayMethod.required = this.checkboxCreateGlEntries.checked;
            this.textboxFrequency.required = this.checkboxCreateGlEntries.checked;
        });

        // Add blur listeners for tax fields to calculate calculated amount
        this.textboxGrossWages.addBlurListener(() => this.calculateCalculatedAmount());
        this.textboxNonTaxableWages.addBlurListener(() => this.calculateCalculatedAmount());
        this.textboxSocialSecurityWithholding.addBlurListener(() => this.calculateCalculatedAmount());
        this.textboxMedicareWithholding.addBlurListener(() => this.calculateCalculatedAmount());
        this.textboxSuiWithholding.addBlurListener(() => this.calculateCalculatedAmount());
        this.textboxFederalWithholding.addBlurListener(() => this.calculateCalculatedAmount());
        this.textboxStateWithholding.addBlurListener(() => this.calculateCalculatedAmount());
        this.textboxCountyWithholding.addBlurListener(() => this.calculateCalculatedAmount());
        this.textboxCityWithholding.addBlurListener(() => this.calculateCalculatedAmount());
        this.textboxSdiWithholding.addBlurListener(() => this.calculateCalculatedAmount());
        this.textboxOldBalApplied.addBlurListener(() => this.calculateCalculatedAmount());

        this.textboxRecordType.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            if (event.hasSelection()) {
                this.setCheckBoxes(event.selection.value);
            }
        });

        this.textboxRegularHours.addBlurListener(() => this.calculateTimecardTotal("off_man_timecard.regular_units", "off_man_timecard.regular_rate", "off_man_timecard.regular_total"));
        this.textboxRegularRate.addBlurListener(() => {
            this.calculateTimecardTotal("off_man_timecard.regular_units", "off_man_timecard.regular_rate", "off_man_timecard.regular_total");
            this.calculateTimecardTotal("off_man_timecard.holiday_units", "off_man_timecard.regular_rate", "off_man_timecard.holiday_total");
            this.calculateTimecardTotal("off_man_timecard.sick_units", "off_man_timecard.regular_rate", "off_man_timecard.sick_total");
        });

        this.textboxOvertimeHours.addBlurListener(() => this.calculateTimecardTotal("off_man_timecard.overtime_units", "off_man_timecard.overtime_rate", "off_man_timecard.overtime_total"));
        this.textboxOvertimeRate.addBlurListener(() => this.calculateTimecardTotal("off_man_timecard.overtime_units", "off_man_timecard.overtime_rate", "off_man_timecard.overtime_total"));

        this.textboxVacationHours.addBlurListener(() => this.calculateTimecardTotal("off_man_timecard.vacation_units", "off_man_timecard.vacation_rate", "off_man_timecard.vacation_total"));
        this.textboxVacationRate.addBlurListener(() => this.calculateTimecardTotal("off_man_timecard.vacation_units", "off_man_timecard.vacation_rate", "off_man_timecard.vacation_total"));

        this.textboxSickHours.addBlurListener(() => this.calculateTimecardTotal("off_man_timecard.sick_units", "off_man_timecard.regular_rate", "off_man_timecard.sick_total"));
        this.textboxSickRate.addBlurListener(() => this.calculateTimecardTotal("off_man_timecard.sick_units", "off_man_timecard.regular_rate", "off_man_timecard.sick_total"));

        this.textboxHolidayHours.addBlurListener(() => this.calculateTimecardTotal("off_man_timecard.holiday_units", "off_man_timecard.regular_rate", "off_man_timecard.holiday_total"));
        this.textboxHolidayRate.addBlurListener(() => this.calculateTimecardTotal("off_man_timecard.holiday_units", "off_man_timecard.regular_rate", "off_man_timecard.holiday_total"));

        this.textboxCheckDate.addBlurListener(() => {
            if (this.mainDataSource.mode !== DataSourceMode.SEARCH) {
                const checkDate = this.activeRow.getDate("check_date");
                const processYear = getPayrollControlValue("current_process_year");
                if (checkDate && checkDate.getFullYear() < processYear && this.activeRow.get("record_type") !== "P") {
                    this.activeRow.set("record_type", "P");
                    this.setCheckBoxes("P");
                    Snackbar.showSnackbar("Record Type was changed to Prior Year Adjustment due to check date in a prior year. This will affect tax reporting (W2s and 1099s) but will not affect the YTD amounts for the payee.", { persist: true, targetPanel: this } );

                }
            }
        });

        this.tablePayAdjustments.addRowDisplayListener((event: TableRowDisplayEvent) => {
            if (this.mainDataSource.mode !== DataSourceMode.SEARCH) {
                const tableRow = event.getTableRow();
                const textboxAmount = tableRow.findComponentById("textboxAmount") as Textbox;
                textboxAmount.enabled = false;
                const textboxType  = tableRow.findComponentById("textboxDeductionType") as Textbox;
                textboxType.enabled = false;
            }

        });

        this.table401kDetails.addRowDisplayListener((event: TableRowDisplayEvent) => {
            const tableRow = event.getTableRow();
            const catchupEnabled = this.fedTaxTable.getBoolean("enable_catchup", false);
            const rothEnabled = this.fedTaxTable.getBoolean("enable_roth", false);

            const textboxContribType = tableRow.findComponentById("textboxContribType") as Textbox;
            if (!catchupEnabled) {
                textboxContribType.removeItemsWithValue("C");
                textboxContribType.removeItemsWithValue("T");
            }
            if (!rothEnabled) {
                textboxContribType.removeItemsWithValue("R");
                textboxContribType.removeItemsWithValue("T");
            }

        });
        this.textboxOffManTimecarddepartment.addChangeListener(() => {
            this.textboxOffManTimecarddepartment.text = this.textboxOffManTimecarddepartment.text.substring(0, 3);
        });
    }


    totalIsValid(): boolean {
        const netPay = this.activeRow.getCurrency("net_pay", new Currency(0));
        const amount = this.calculateCalculatedAmount();
        return netPay.isEqualTo(amount);
    }

    isGrossAndTimecardValid():  {isValid: boolean, errorString?: string}  {
        const timecardRow = this.activeRow;
        const unitFields = [
            "off_man_timecard.holiday_units",
            "off_man_timecard.sick_units",
            "off_man_timecard.vacation_units",
            "off_man_timecard.overtime_units",
            "off_man_timecard.regular_units"
        ];

        const hasTimecardEntries = unitFields.some(field =>
            !timecardRow.isNull(field) && !timecardRow.getCurrency(field, new Currency(0)).isZero()
        );

        const timecardTotal = new Currency(timecardRow.getCurrency("off_man_timecard.regular_total", new Currency(0)));
        timecardTotal.add(timecardRow.getCurrency("off_man_timecard.overtime_total", new Currency(0)));
        timecardTotal.add(timecardRow.getCurrency("off_man_timecard.vacation_total", new Currency(0)));
        timecardTotal.add(timecardRow.getCurrency("off_man_timecard.sick_total", new Currency(0)));
        timecardTotal.add(timecardRow.getCurrency("off_man_timecard.holiday_total", new Currency(0)));
        const timecardAmount = new Currency(timecardTotal);
        timecardTotal.add(this.earningsTotal)



        if (!this.activeRow.getBoolean("post_to_gl") && !hasTimecardEntries) {
            return {isValid: true};
        }

        if (!timecardTotal.isEqualTo(this.activeRow.getCurrency("tot_gross_pay", new Currency(0)))) {
            const prefix = timecardTotal.isZero() ? "When \"Create GL entries\" is checked, " : "";
            const errorString = prefix + `Gross Wages (${this.activeRow.getCurrency("tot_gross_pay", new Currency(0))}) must equal Timecard Total Pay (${timecardAmount}) plus Pay Adjustments Total Earnings (${this.earningsTotal}).`;
            return {isValid: false, errorString};
        }

        return {isValid: true};
    }

    /** This is an event handler for the onClick event of buttonJobClassification. */
    async buttonJobClassificationOnClick(event: ClickEvent) {
        const layout = Layout.getLayout("lme/settlement/JobClassLookup") as AutogenLayoutJobClassLookup;
        CommonDialogs.showDialog(layout, {
            title: "Job Classification Lookup",
            okProps: {
                onClick: () => {
                    if (layout.tableJobClass.selectedRow != null) {
                        const selectedRow = layout.tableJobClass.selectedRow.data as ModelRow;
                        this.activeRow.setLookupModelData("off_man_timecard.job_class_code_id", selectedRow.getLookupModelData("id"));
                        this.activeRow.set("off_man_timecard.job_class_code_id", selectedRow.get("id"));
                        this.activeRow.setLookupModelData("off_man_timecard.division_id", selectedRow.getLookupModelData("division_id"));
                        this.activeRow.set("off_man_timecard.division_id", selectedRow.get("division_id"));
                        this.activeRow.setLookupModelData("off_man_timecard.terminal_id", selectedRow.getLookupModelData("terminal_id"));
                        this.activeRow.set("off_man_timecard.terminal_id", selectedRow.get("terminal_id"));
                        this.buttonClear.enabled = true;
                    }
                }
            }
        });
    }

    buttonClearClassificationOnClick(event: ClickEvent) {
        this.activeRow.setLookupModelData("off_man_timecard.job_class_code_id", null);
        this.activeRow.set("off_man_timecard.job_class_code_id", null);
        this.activeRow.setLookupModelData("off_man_timecard.division_id", null);
        this.activeRow.set("off_man_timecard.division_id", null);
        this.activeRow.setLookupModelData("off_man_timecard.terminal_id", null);
        this.activeRow.set("off_man_timecard.terminal_id", null);
        this.buttonClear.enabled = false;
    }

    private calcTotals(): void {
        // Guard: require an active record.
        if (!this.activeRow) return;

        const totalDeductions = new Currency(0);
        const totalEarnings = new Currency(0);
        const totalReimbursements = new Currency(0);

        for (const row of this.tablePayAdjustments.data) {
            const amount = row.getCurrency("amount", new Currency(0));

            const type = row.get("deduction_type");
            if (type == DEDUCT_TYPE_EARNING) {
                totalEarnings.add(new Currency(amount));
            } else if (type != DEDUCT_TYPE_REIMBURSE) {
                totalDeductions.add(new Currency(amount));
            } else {
                totalReimbursements.add(new Currency(amount));
            }
        }

        // Employer liability / additional deduction fields (if present on active row).
        const addActiveRowCurrency = (field: string) => {
            if (!this.activeRow?.isNull?.(field)) {
                totalDeductions.add(this.activeRow.getCurrency(field, new Currency(0)));
            }
        };

        addActiveRowCurrency("tot_trailer_rent");
        addActiveRowCurrency("old_bal_applied");

        // Subtract reimbursements per original logic.
        totalDeductions.subtract(totalReimbursements);
        this.activeRow.set("other_deduct", totalDeductions);
        this.deductionTotal = new Currency(totalDeductions);
        this.earningsTotal = new Currency(totalEarnings);
        this.textboxTotalPayAdjustmentsReimbursements.text = totalDeductions.toString();
        this.textboxTotalEarnings.text = totalEarnings.toString();
        this.textboxDetailLines.text = this.tablePayAdjustments.data.length.toString();

        // --- 401k Grid Totals ---
        const contrib = new Currency(0); // regular + roth
        const catchup = new Currency(0); // catch-up
        const match = new Currency(0);

        for (const row of this.table401kDetails.data) {
            const type = row.getString?.("contrib_type", CONTRIB_401K);
            const contribution = row.getCurrency("contribution", new Currency(0));
            const matching = row.getCurrency("matching_amount", new Currency(0));
            if (type == CONTRIB_401K || type == CONTRIB_ROTH) {
                contrib.add(contribution);
            } else {
                catchup.add(contribution);
            }
            match.add(matching);
        }

        this.activeRow.set("e401k_wh", contrib);
        this.activeRow.set("e401k_catchup_wh", catchup);
        this.activeRow.set("employer_match", match);

        const totalContrib = new Currency(contrib);
        totalContrib.add(catchup);
        this.contribution401k = new Currency(contrib);
        this.contribution401kCatchup = new Currency(catchup);
        this.textboxTotalPayeeContribution.text = totalContrib.toString();
        this.textboxTotalEmployerMatch.text = match.toString();

        this.calculateCalculatedAmount();
    }

    calculateCalculatedAmount() : Currency {

        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;

        const row = this.activeRow;
        const calculatedTotal = new Currency(row.getCurrency("nontax_pay", new Currency(0)));
        const fieldNames = [
            "fica_ss_wh",
            "fica_hi_wh",
            "sui_wh",
            "fed_tax_wh",
            "state_tax_wh",
            "county_tax_wh",
            "city_tax_wh",
            "sdi_wh",
            "puc_tax_wh"
        ];

        for (const fieldName of fieldNames) {
            calculatedTotal.subtract(row.getCurrency(fieldName, new Currency(0)));
        }
        calculatedTotal.subtract(this.deductionTotal);
        calculatedTotal.subtract(this.contribution401k);
        calculatedTotal.subtract(this.contribution401kCatchup);
        calculatedTotal.add(row.getCurrency("tot_gross_pay", new Currency(0)));


        this.textboxCalculatedAmount.text = calculatedTotal.toString();
        return calculatedTotal;

    }

    calculateTimecardTotal(unitsField: string, rateField: string, totalField: string) {
        const units = this.activeRow.get(unitsField, 0);
        const rate = this.activeRow.get(rateField, 0);
        const total = units * rate;
        const currencyTotal = new Currency(total);
        this.activeRow.set(totalField, currencyTotal);
        this.setTimeCardTotalPay();
    }

    setCheckBoxes(recordType: string) {
        const check = ["M", "A"].includes(recordType);
        this.checkboxCreateCheckRecord.checked = check;
        this.checkboxCreateGlEntries.checked = check;
    }

    setTimeCardTotalPay() {
        const totalFields = [
            { totalField: "off_man_timecard.regular_total", hoursField: "off_man_timecard.regular_units" },
            { totalField: "off_man_timecard.overtime_total", hoursField: "off_man_timecard.overtime_units" },
            { totalField: "off_man_timecard.vacation_total", hoursField: "off_man_timecard.vacation_units" },
            { totalField: "off_man_timecard.sick_total", hoursField: "off_man_timecard.sick_units" },
            { totalField: "off_man_timecard.holiday_total", hoursField: "off_man_timecard.holiday_units" }
        ];
        const total = new Currency(0);
        let hours = 0;
        for (const field of totalFields) {
            total.add(this.activeRow.getCurrency(field.totalField, new Currency(0)));
            hours += this.activeRow.get(field.hoursField, 0);
        }
        this.activeRow.set("off_man_timecard.total_pay", total);
        this.activeRow.set("off_man_timecard.total_hours", hours);
    }

    unitsOnDisplay(event: DataDisplayEvent) {
        const textbox = event.target as Textbox;
        textbox.captionProps = { align: HorizontalAlignment.RIGHT, fontBold: true, color: "default" };
    }

    /** This is an event handler for the beforeLookupModelSearch event of payee. */
    payeeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.office_employee = "Y";
    }

    /** This is an event handler for the onLookupModelSelection event of payee. */
    payeeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection( )&& this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            const selectedRow = event.selection as ModelRow;
            const payeeType = selectedRow.get("off_payee.type_of");
            this.activeRow.set("off_payee.type_of", payeeType);

            this.activeRow.setValueAndLookupModelData("state_tax_id", selectedRow.get("state_tax_code"), selectedRow.getLookupModelData("state_tax_code"));
            this.activeRow.setValueAndLookupModelData("city_tax_id", selectedRow.get("city_tax_code"), selectedRow.getLookupModelData("city_tax_code"));
            this.activeRow.setValueAndLookupModelData("county_tax_id", selectedRow.get("county_tax_code"), selectedRow.getLookupModelData("county_tax_code"));
            this.activeRow.setValueAndLookupModelData("sui_tax_code", selectedRow.get("sui_tax_code"), selectedRow.getLookupModelData("sui_tax_code"));


            if([HOURLY_PAY_METHOD, SALARY_PAY_METHOD].includes(selectedRow.get("off_payee.office_paymethod"))) {
                this.activeRow.set("off_man_timecard.pay_method", selectedRow.get("off_payee.office_paymethod"));
            }

            if(PAY_FREQUENCY_CODES.includes(selectedRow.get("off_payee.payment_frequency"))) {
                this.activeRow.set("off_man_timecard.payment_frequency", selectedRow.get("off_payee.payment_frequency"));
            }


            this.activeRow.set("off_man_timecard.regular_rate", selectedRow.get("off_payee.regular_rate"));
            this.calculateTimecardTotal("off_man_timecard.regular_units", "off_man_timecard.regular_rate", "off_man_timecard.regular_total");
            this.calculateTimecardTotal("off_man_timecard.holiday_units", "off_man_timecard.regular_rate", "off_man_timecard.holiday_total");
            this.calculateTimecardTotal("off_man_timecard.sick_units", "off_man_timecard.regular_rate", "off_man_timecard.sick_total");

            this.activeRow.set("off_man_timecard.overtime_rate", selectedRow.get("off_payee.overtime_rate"));
            this.calculateTimecardTotal("off_man_timecard.overtime_units", "off_man_timecard.overtime_rate", "off_man_timecard.overtime_total");

            this.activeRow.set("off_man_timecard.vacation_rate", selectedRow.get("off_payee.vacation_pay_rate"));
            this.calculateTimecardTotal("off_man_timecard.vacation_units", "off_man_timecard.vacation_rate", "off_man_timecard.vacation_total");

            this.activeRow.setValueAndLookupModelData("off_man_timecard.department", selectedRow.get("off_payee.department"), {department: selectedRow.get("off_payee.department")});

            //Add Classification info
            this.activeRow.setLookupModelData("off_man_timecard.division_id", selectedRow.getLookupModelData("off_payee.division_id"));
            this.activeRow.set("off_man_timecard.division_id", selectedRow.get("off_payee.division_id"));

            this.activeRow.setLookupModelData("off_man_timecard.terminal_id", selectedRow.getLookupModelData("off_payee.terminal_id"));
            this.activeRow.set("off_man_timecard.terminal_id", selectedRow.get("off_payee.terminal_id"));

            this.activeRow.setLookupModelData("off_man_timecard.job_class_code_id", selectedRow.getLookupModelData("off_payee.job_class_code_id"));
            this.activeRow.set("off_man_timecard.job_class_code_id", selectedRow.get("off_payee.job_class_code_id"));

            this.buttonClear.enabled = !this.activeRow.isNull("off_man_timecard.job_class_code_id") || !this.activeRow.isNull("off_man_timecard.division_id") || !this.activeRow.isNull("off_man_timecard.terminal_id");

        }
        else {
            this.activeRow.set("off_payee.type_of", null);
        }
    }

    /** This is an event handler for the onBlur event of checkNumber. */
    checkNumberOnBlur(event: BlurEvent) {
        const checkNum = this.activeRow.get("check_number");
        if (!StringUtil.isEmptyString(checkNum)) {
            this.activeRow.set("check_number", checkNum.toString().padStart(8, '0'));
        }
    }

    addValidations() {
        this.textboxStateTaxId.addlValidationCallback = (_value: string, _mode: DataSourceMode): ValidationResult => {
            const result: ValidationResult = {
                component: this.textboxStateTaxId,
                isValid: true
            };

            // Collect all validation messages so user sees every missing code requirement at once.
            const messages: string[] = [];

            // Existing rule for state wages.
            if (!this.activeRow.isNull("state_wages") && this.activeRow.isNull("state_tax_id")) {
                messages.push("State Tax Code is required for State Wages.");
            }

            // State withholding requires State Tax Code.
            if (!this.activeRow.isNull("state_tax_wh") && this.activeRow.isNull("state_tax_id")) {
                messages.push("State Tax Code is required for State Tax withheld.");
            }

            // SUI withholding requires State Tax Code OR SUI Tax Code (either one missing triggers requirement per spec).
            if (!this.activeRow.isNull("sui_wh") && (this.activeRow.isNull("state_tax_id") && this.activeRow.isNull("sui_tax_code"))) {
                messages.push("State tax code or SUI tax code is required for SUI Tax withheld.");
            }

            // SDI withholding requires State Tax Code.
            if (!this.activeRow.isNull("sdi_wh") && this.activeRow.isNull("state_tax_id")) {
                messages.push("State tax code is required for SDI Tax withheld.");
            }

            if (!this.activeRow.isNull("employer_sdi") && this.activeRow.isNull("state_tax_id")) {
                messages.push("State tax code is required for Employer SDI Liability.");
            }

            if (!this.activeRow.isNull("employer_sui") && (this.activeRow.isNull("state_tax_id") && this.activeRow.isNull("sui_tax_code"))) {
                messages.push("State tax code or SUI tax code is required for Employer SUI Liability");
            }

            if(!this.activeRow.isNull("pfml_wages") && this.activeRow.isNull("state_tax_id")) {
                messages.push("State Tax Code is required for PFML Wages.");
            }

            if (messages.length > 0) {
                result.isValid = false;
                // Join messages with a space. Could also use '\n' if multiline display is supported.
                const errors = messages.join("\n\n");
                result.validationMessage = errors
                result.caption = errors
            }
            return result;
        }
        this.textboxCityTaxId.addlValidationCallback = (_value: string, _mode: DataSourceMode): ValidationResult => {
            const result: ValidationResult = {
                component: this.textboxCityTaxId,
                isValid: true
            };
            // Collect all validation messages so user sees every missing code requirement at once.
            const messages: string[] = [];
            // City withholding requires City Tax Code.
            if (!this.activeRow.isNull("city_tax_wh") && this.activeRow.isNull("city_tax_id")) {
                messages.push("City Tax Code is required for City Tax withheld.");
            }

            if (!this.activeRow.isNull("city_wages") && this.activeRow.isNull("city_tax_id")) {
                messages.push("City Tax Code is required for City Wages.");
            }

            if (messages.length > 0) {
                result.isValid = false;
                // Join messages with a space. Could also use '\n' if multiline display is supported.
                const errors = messages.join("\n\n");
                result.validationMessage = errors
                result.caption = errors
            }
            return result;
        }

        this.textboxCountyTaxId.addlValidationCallback = (_value: string, _mode: DataSourceMode): ValidationResult => {
            const result: ValidationResult = {
                component: this.textboxCountyTaxId,
                isValid: true
            };
            // Collect all validation messages so user sees every missing code requirement at once.
            const messages: string[] = [];
            // County withholding requires County Tax Code.
            if (!this.activeRow.isNull("county_tax_wh") && this.activeRow.isNull("county_tax_id")) {
                messages.push("County Tax Code is required for County Tax withheld.");
            }

            if (!this.activeRow.isNull("county_wages") && this.activeRow.isNull("county_tax_id")) {
                messages.push("County Tax Code is required for County Wages.");
            }

            if (messages.length > 0) {
                result.isValid = false;
                // Join messages with a space. Could also use '\n' if multiline display is supported.
                const errors = messages.join("\n\n");
                result.validationMessage = errors
                result.caption = errors
            }
            return result;
        }
        this.mainDataSource.addValidationListener((event: DataSourceValidationEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
                return;
            }
            if (this.activeRow.getBoolean("post_to_gl") && !this.activeRow.getCurrency("off_man_timecard.total_pay", new Currency(0)).isZero()) {
                if (this.activeRow.isNull("off_man_timecard.division_id")) {
                    event.validationResults.push({
                        isValid: false,
                        component: this.textboxDivision,
                        validationMessage: this.textboxDivision.caption
                    });
                }
                if (this.activeRow.isNull("off_man_timecard.terminal_id")) {
                    event.validationResults.push({
                        isValid: false,
                        component: this.textboxTerminal,
                        validationMessage: this.textboxTerminal.caption
                    });
                }
                if (this.activeRow.isNull("off_man_timecard.job_class_code_id")) {
                    event.validationResults.push({
                        isValid: false,
                        component: this.textboxJobClass,
                        validationMessage: this.textboxJobClass.caption
                    });
                }
            }

        });
         this.mainDataSource.addValidationListener((event: DataSourceValidationEvent) => {
            if (this.mainDataSource.mode !== DataSourceMode.SEARCH && !this.totalIsValid()) {
                event.validationResults.push({
                    isValid: false,
                    component: this.textboxCheckAmount,
                    caption: "Gross Wages minus Deductions (" + this.calculateCalculatedAmount() + ") must match Check Amount (" + this.activeRow.getCurrency("net_pay", new Currency(0)) + ")."
                });
            }
            const grossAndTimecardValid = this.isGrossAndTimecardValid();
            if (!grossAndTimecardValid.isValid) {
                event.validationResults.push({
                    isValid: false,
                    component: this.activeRow.get("drs_payee.type_of") !== "C" ? this.textboxNonTaxableWages : this.textboxGrossWages,
                    caption: grossAndTimecardValid.errorString
                });
            }
        });

    }

    payAdjValueOnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        const textbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textbox);
        const modelRow = tableRow.data as ModelRow;
        const units = modelRow.get("units", 0);
        const rate = modelRow.get("rate", 0);

        modelRow.set("amount", new Currency(units * rate));
        if (this.tablePayAdjustments.quickAddRow != tableRow) {
            this.calcTotals();
        } else {
            // setting the amount does not auto-update the textbox in quick add row
            const textboxAmount = tableRow.findComponentById("textboxAmount") as Textbox;
            textboxAmount.displayData(modelRow, null, 0);
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxDeductCodeId. */
    textboxDeductCodeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        const textbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textbox);
        const modelRow = tableRow.data as ModelRow;
        if (event.hasSelection()) {
            const selectedRow = event.selection as ModelRow;
            modelRow.set("short_desc", selectedRow.get("short_desc"));
            modelRow.set("deduction_type", selectedRow.get("code_type"));
            modelRow.setLookupModelData("glid", selectedRow.getLookupModelData("glid"));
            modelRow.set("glid", selectedRow.get("glid"));
        }
        else {
            modelRow.set("short_desc", null);
            modelRow.set("deduction_type", null);
            modelRow.set("glid", null);
        }
        const textboxGLAccount = tableRow.findComponentById("textboxGlid") as Textbox;
        textboxGLAccount.displayData(modelRow, null, 0);
        const textboxType = tableRow.findComponentById("textboxDeductionType") as Textbox;
        textboxType.displayData(modelRow, null, 0);
        const textboxShortDesc = tableRow.findComponentById("textboxShortDesc") as Textbox;
        textboxShortDesc.displayData(modelRow, null, 0);
    }

     /** This is an event handler for the afterDropdownSelection event of textboxTaxType. */
    textboxTaxTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        const textbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textbox);
        const textboxTaxCode = tableRow.findComponentById("textboxTaxCode") as Textbox;
        textboxTaxCode.required = event.hasSelection();
    }

}
